import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { encrypttoHEX } from "./encodedecode.js";
import { ArrowBack } from "@mui/icons-material";
import { Avatar } from "@mui/material";
import Logo from "../assets/images/commaLogo.png";
import { LogOut } from "lucide-react";

export default function FeedConfiguration() {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we have the required data from CommaFeedConstruction
  if (!location.state || !location.state.tasks || !location.state.feedType) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-red-600">
          Invalid configuration. Please start from the feed construction page.
        </div>
      </div>
    );
  }

  const { tasks, feedType } = location.state;
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [feedURL, setFeedURL] = useState("");
  const baseURL = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ":" + window.location.port : ""}/`;
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [verifyingDoctor, setVerifyingDoctor] = useState(true);

  const [patients, setPatients] = useState([]);

  // Fetch patients for the doctor
  useEffect(() => {
    verifyDoctorAccess();
    axios.get('https://genz-staging.feeltiptop.com/api/doctor/patients', {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    }).then(res => {
      setPatients(res.data.patients || []);
    });
  }, []);

  const [feedConfig, setFeedConfig] = useState({
    name: "",
    sessionLink: "",
    sessionStartTime: new Date().toISOString().slice(0, 16),
    sessionEndTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
    maxStarted: 1,
    maxCompleted: 1,
    patientId: ""
  });

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      "Content-Type": "application/json",
    },
  });

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const verifyDoctorAccess = async () => {
    if (!localStorage.getItem("authToken")) {
      setVerifyingDoctor(false);
      console.error("Error verifying doctor status:", error);
      return;
    }

    try {
      const response = await axios.get(
        "https://genz-staging.feeltiptop.com/api/doctor/info",
        getAuthHeaders(),
      );

      if (response.data.success) {
        setDoctorInfo(response.data.doctor);
      }
    } catch (error) {
      console.error("Error verifying doctor status:", error);
    } finally {
      setVerifyingDoctor(false);
    }
  };


  const createObfuscatedFeedURL = (feedID) => {
    const encryptSTR = JSON.stringify({ feedID: feedID });
    return "Login?p=" + encrypttoHEX(encryptSTR);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFeedConfig((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Format dates properly by removing milliseconds and 'Z'
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().split(".")[0]; // Remove milliseconds and Z
      };

      // Create the feed first
      const feedResponse = await axios.post(
        "https://genz-staging.feeltiptop.com/api/feed/create",
        {
          Name: feedConfig.name,
          SessionLink: feedConfig.sessionLink,
          SessionStartTime: formatDate(feedConfig.sessionStartTime),
          SessionEndTime: formatDate(feedConfig.sessionEndTime),
          MaxStarted: parseInt(feedConfig.maxStarted),
          MaxCompleted: parseInt(feedConfig.maxCompleted),
          FeedType: feedType,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (feedResponse.data.success) {
        const feedId = feedResponse.data.feedId;

        // Add assets to the feed with their positions
        const assetPositions = [];
        tasks.forEach((task, index) => {
          if (task.asset) {
            // Create an array of {assetId, position} objects to maintain order
            assetPositions.push({
              assetId: task.asset.assetId,
              position: index + 1
            });
          }
        });

        await axios.post(
          `https://genz-staging.feeltiptop.com/api/feed/${feedId}/assets/add`,
          {
            assets: assetPositions,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        // Generate magic link for the selected patient
        const magicLinkRes = await axios.post(
          "https://genz-staging.feeltiptop.com/api/feed/magic-link",
          {
            feedId,
            patientId: feedConfig.patientId,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (magicLinkRes.data.success) {
          setFeedURL(magicLinkRes.data.link);
        }

        // Add the feed URL (optional, if you want to store it in your DB)
        await axios.post(
          "https://genz-staging.feeltiptop.com/api/addFeedURL",
          {
            FeedID: feedId,
            SessionLink: magicLinkRes.data.link,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        setSuccess(true);
      }
    } catch (err) {
      setError(err.message || "Failed to create feed");
      console.error("Error creating feed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Navbar */}
      <nav className="flex items-center justify-between bg-white px-6 py-4 shadow">
        <div
          className="group relative flex items-center"
          style={{ minHeight: 80 }}
        >
          {/* Logo and Title */}
          <div
            className="duration-400 flex items-center transition-all ease-in-out"
            style={{}}
          >
            {/* Animated Back Button */}
            <button
              onClick={() => navigate("/CommaDashboard")}
              className="duration-400 pointer-events-none z-10 -translate-x-14 transform transition-all ease-in-out group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100"
            >
              <ArrowBack className="h-6 w-6 text-gray-600" />
            </button>
            <button
            onClick={() => navigate("/CommaDashboard")}
            >
              <img
                src={Logo}
                alt="COMMA logo"
                className="w-85 duration-400 h-20 transition-all ease-in-out group-hover:ml-5"
                style={{
                  transition: "margin-left 0.4s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </button>
            <div className="duration-400 group-hover:mHei4 ml-4 text-2xl font-semibold text-gray-800 transition-all ease-in-out">
              Assessment Configuration
            </div>
          </div>
          
        </div>
          <div className="flex items-center space-x-4">
            <Avatar
              src={`https://api.dicebear.com/7.x/initials/svg?seed=${doctorInfo?.firstName + " " + doctorInfo?.lastName}`}
              alt={doctorInfo?.firstName + " " + doctorInfo?.lastName}
              sx={{ width: 50, height: 50 }} // MUI uses pixels
            />
            <span className="text-lg font-bold text-gray-700">
              Dr. {doctorInfo?.firstName} {doctorInfo?.lastName}
            </span>
            <button onClick={handleLogout}>
              <LogOut className="h-6 w-6 text-gray-600" />
            </button>
            {/* Animated Back Button */}
            <button
              onClick={() => navigate("/CommaDashboard")}
              className="duration-400 pointer-events-none z-10 -translate-x-14 transform opacity-0 transition-all"
            >
              <ArrowBack className="h-6 w-6 text-gray-600" />
            </button>
          </div>
      </nav>

      <div className="mt-2 mb-2 flex items-center justify-end md:justify-end px-2">
        {error && (
          <div className="rounded bg-red-100 p-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-800">
          <p className="font-medium">Error</p>
          <p>{error}</p>
        </div>
      )}

{success && feedURL && (
  <div className="mb-6 mx-auto w-full max-w-2xl rounded-lg bg-green-50 p-6 text-center">
    <h2 className="mb-2 text-lg font-semibold text-green-800">
      Feed Created Successfully!
    </h2>
    <p className="mb-4 text-green-700">
      Your feed session link is ready:
    </p>
    <div className="flex flex-col items-center w-full md:w-auto space-y-4 md:flex-row md:space-x-4 md:space-y-0">
      <a
        href={feedURL}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full text-center break-all text-blue-600 hover:text-blue-800 hover:underline"
      >
        {feedURL}
      </a>
      <button
        onClick={() => navigator.clipboard.writeText(feedURL)}
        className="rounded bg-blue-100 px-3 py-1 text-blue-700 hover:bg-blue-200"
        title="Copy to clipboard"
      >
        Copy
      </button>
    </div>
    <div className="mt-4 flex justify-center">
      <button
        onClick={() => navigate(`/feed-session?p=${feedURL.split('p=')[1]}`)}
        className="rounded-lg bg-green-600 px-6 py-2 text-white hover:bg-green-700"
      >
        Go to Feed
      </button>
    </div>
  </div>
)}

      {!success && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-lg font-bold text-gray-700 text-left">
              Select Patient:
            </label>
            <select
              name="patientId"
              value={feedConfig.patientId || ""}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
            >
              <option value="">-- Select Patient --</option>
              {patients.map((p) => (
                <option key={p.userId} value={p.userId}>
                  {p.fullName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-lg font-bold text-gray-700 text-left">
              Feed Name:
            </label>
            <input
              type="text"
              name="name"
              value={feedConfig.name}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter feed name"
            />
          </div>

          <div>
            <label className="block text-lg font-bold text-gray-700 text-left">
              Session Link:
            </label>
            <input
              type="text"
              name="sessionLink"
              value={feedConfig.sessionLink}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter session link (optional)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-lg font-bold text-gray-700 text-left">
                Session Start Time (Default is now):
              </label>
              <input
                type="datetime-local"
                name="sessionStartTime"
                value={feedConfig.sessionStartTime}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-gray-700 text-left">
                Session End Time (Default is in 1 week):
              </label>
              <input
                type="datetime-local"
                name="sessionEndTime"
                value={feedConfig.sessionEndTime}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-lg font-bold text-gray-700 text-left">
                Max Started
              </label>
              <input
                type="number"
                name="maxStarted"
                value={feedConfig.maxStarted}
                onChange={handleInputChange}
                required
                min="1"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-gray-700 text-left">
                Max Completed
              </label>
              <input
                type="number"
                name="maxCompleted"
                value={feedConfig.maxCompleted}
                onChange={handleInputChange}
                required
                min="1"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-lg border border-gray-300 bg-white px-6 py-3 text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700 disabled:bg-blue-400"
            >
              {loading ? "Creating Feed..." : "Create Feed"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
